import { useQuery } from "@tanstack/react-query";
import { ErrorMessage, Field, Form, Formik } from "formik";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useAuth } from "../../../../modules/auth";
import { FileUpload } from "../../../registration/helper/FileUpload";
import { useSchemesMaster } from "../../../registration/hooks/useMasterData";
import { uploadSchemeReport } from "../../api/SchemeDataEntry.api";
import {
	useSchemeSubmit,
	useSchemeUpdate,
} from "../../hooks/useSchemeDataEntry";
import { fetchDrpListByDistrict, generateHlcRegistrationNo } from "./hlc.api";

type Props = {
	isView?: boolean;
	districts: any[];
	schemeId?: number;
	id?: string;
	initialData?: any;
};

// Which scheme this HLC meeting's paras belong to — a curated subset (not
// the full schemes master list), matching the shared reference format.
// MGNREGS, PMAY-G and 15th CFC Grant share the same built form; "Other" is
// still a placeholder.
const TARGET_SCHEMES = ["MGNREGS", "PMAY-G", "15th CFC Grant", "Other"];

// Schemes that use the fully built HLC form below (vs. the "Other"
// under-construction placeholder).
const BUILT_TARGET_SCHEMES = ["MGNREGS", "PMAY-G", "15th CFC Grant"];

const validationSchema = Yup.object({
	district_id: Yup.string().required("District is required"),
	drp_employee_id: Yup.string().required("DRP/DRP I/C Name is required"),
	hlc_no: Yup.string().required("HLC No. is required"),
	hlc_date: Yup.string().required("HLC Date is required"),
	proceeding_no: Yup.string().required("Proceeding No. is required"),
	proceeding_date: Yup.string().required("Proceeding Date is required"),
});

const initialValues = {
	brp_employee_code: "",
	brp_name: "",
	brp_mobile_no: "",
	district_id: "",
	drp_employee_id: "",
	hlc_no: "",
	hlc_date: "",
	proceeding_no: "",
	proceeding_date: "",
	paras_placed: 0,
	paras_closed: 0,
	fm_paras: 0,
	fm_amount: 0,
	fd_paras: 0,
	fd_amount: 0,
	pv_paras: 0,
	pv_amount: 0,
	gr_paras: 0,
	gr_amount: 0,
	recovered_amount: 0,
	fir: "2",
	charges: "2",
	action_taken_details: "",
	hlc_minutes_file: null as string | null,
	registration_no: "",
};

const HLCForm: React.FC<Props> = ({
	isView = false,
	districts,
	schemeId,
	id,
	initialData,
}) => {
	const navigate = useNavigate();
	const { currentUser } = useAuth() as any;
	const submitMutation = useSchemeSubmit();
	const updateMutation = useSchemeUpdate();
	const { data: schemesMaster = [] } = useSchemesMaster();
	const [generating, setGenerating] = useState(false);
	const [generated, setGenerated] = useState(!!initialData?.registration_no);
	const [targetScheme, setTargetScheme] = useState(
		initialData?.target_scheme_name || "MGNREGS",
	);

	// Real scheme_id (from the schemes master table) for whichever scheme
	// was picked via the inner tab bar — saved into json_data alongside
	// target_scheme_name so reports can join back to the schemes table.
	const targetSchemeId = schemesMaster.find(
		(s: any) => s.name === targetScheme,
	)?.id;

	const mergedInitialValues = {
		...initialValues,
		brp_employee_code:
			initialData?.brp_employee_code || currentUser?.employee_code || "",
		brp_name: initialData?.brp_name || currentUser?.name || "",
		brp_mobile_no: initialData?.brp_mobile_no || currentUser?.mobile || "",
		...(initialData || {}),
	};

	const onSubmit = async (values: any, actions: any) => {
		if (!values.registration_no) {
			toast.error("Please generate the Registration No. first");
			actions.setSubmitting(false);
			return;
		}
		try {
			const dataToSave = {
				...values,
				scheme_id: schemeId,
				target_scheme_name: targetScheme,
				target_scheme_id: targetSchemeId,
			};
			if (id) {
				await updateMutation.mutateAsync({ id, data: dataToSave });
				toast.success("HLC entry updated successfully");
			} else {
				await submitMutation.mutateAsync(dataToSave);
				toast.success("HLC entry submitted successfully");
			}
			navigate("/scheme-data-entry");
		} catch (error: any) {
			toast.error(
				error.response?.data?.message ||
					error.response?.data?.error ||
					error.message ||
					"Failed to submit HLC entry",
			);
		} finally {
			actions.setSubmitting(false);
		}
	};

	return (
		<div className="card shadow-sm mb-5">
			<div className="card-header border-0 pt-5">
				<h3 className="card-title align-items-start flex-column">
					<span className="card-label fw-bold fs-3 mb-1">HLC Entry Form</span>
					<span className="text-muted mt-1 fw-semibold fs-7">
						Enter High-Level Committee meeting details for each scheme.
					</span>
				</h3>
			</div>
			<div className="card-body py-3">
				{/* Which scheme this HLC meeting's paras belong to — MGNREGS,
				    PMAY-G and 15th CFC Grant share the built form below; Other
				    is still a placeholder. */}
				<div className="d-flex flex-wrap border rounded mb-8 overflow-hidden">
					{TARGET_SCHEMES.map((name) => {
						const isActive = targetScheme === name;
						return (
							<button
								key={name}
								type="button"
								className={`btn rounded-0 flex-grow-1 fw-bold ${
									isActive
										? "btn-light"
										: "btn-secondary bg-light-secondary text-gray-600"
								}`}
								onClick={() => setTargetScheme(name)}
							>
								{name}
							</button>
						);
					})}
				</div>

				{!BUILT_TARGET_SCHEMES.includes(targetScheme) ? (
					<div className="text-center text-muted py-20 fw-semibold">
						The form for {targetScheme} is currently under construction. Please
						check back later.
					</div>
				) : (
				<Formik
					initialValues={mergedInitialValues}
					validationSchema={validationSchema}
					onSubmit={onSubmit}
					enableReinitialize={true}
				>
					{({ values, errors, touched, setFieldValue, isSubmitting }) => {
						const { data: drpList = [] } = useQuery({
							queryKey: ["hlc-drp-list", values.district_id],
							queryFn: () => fetchDrpListByDistrict(values.district_id),
							enabled: !!values.district_id,
						});

						const canGenerate =
							!isView &&
							!generated &&
							!!values.district_id &&
							!!values.hlc_no &&
							!!values.proceeding_no &&
							!!values.proceeding_date;

						const handleGenerate = async () => {
							setGenerating(true);
							try {
								const regNo = await generateHlcRegistrationNo({
									scheme_id: schemeId as number,
									target_scheme_name: targetScheme,
									district_id: values.district_id,
									hlc_no: values.hlc_no,
									proceeding_no: values.proceeding_no,
									proceeding_date: values.proceeding_date,
								});
								if (regNo) {
									setFieldValue("registration_no", regNo);
									setGenerated(true);
									toast.success("Registration number generated successfully");
								}
							} catch (error: any) {
								toast.error(
									error.response?.data?.message ||
										"Failed to generate registration number",
								);
							} finally {
								setGenerating(false);
							}
						};

						const pending =
							(Number(values.paras_placed) || 0) -
							(Number(values.paras_closed) || 0);

						return (
							<Form>
								{/* Basic Details (logged-in user) */}
								<div className="mb-10 border border-dashed border-gray-300 p-8 rounded-3">
									<h4 className="fw-bold text-gray-800 mb-6">Basic Details</h4>
									<div className="row g-9">
										<div className="col-md-4 fv-row">
											<label className="form-label required fs-6 fw-bold">
												Employee Code
											</label>
											<Field
												name="brp_employee_code"
												className="form-control form-control-solid"
												disabled={true}
												readOnly={true}
											/>
										</div>
										<div className="col-md-4 fv-row">
											<label className="form-label fs-6 fw-bold">Name</label>
											<Field
												name="brp_name"
												className="form-control form-control-solid"
												disabled={true}
												readOnly={true}
											/>
										</div>
										<div className="col-md-4 fv-row">
											<label className="form-label fs-6 fw-bold">Mobile No</label>
											<Field
												name="brp_mobile_no"
												className="form-control form-control-solid"
												disabled={true}
												readOnly={true}
											/>
										</div>
									</div>
								</div>

								{/* HLC Details */}
								<div className="mb-10 border border-dashed border-gray-300 p-8 rounded-3">
									<h4 className="fw-bold text-gray-800 mb-6">HLC Details</h4>
									<div className="row g-9 mb-5">
										<div className="col-md-3 fv-row">
											<label className="form-label required fs-6 fw-bold">
												District
											</label>
											<Field
												as="select"
												name="district_id"
												className="form-select"
												disabled={isView}
												onChange={(e: any) => {
													setFieldValue(
														"district_id",
														e.target.value ? Number(e.target.value) : "",
													);
													setFieldValue("drp_employee_id", "");
												}}
											>
												<option value="">Select District</option>
												{(districts || []).map((d: any) => (
													<option key={d.id} value={d.id}>
														{d.name}
													</option>
												))}
											</Field>
											<ErrorMessage
												name="district_id"
												component="div"
												className="text-danger fs-8"
											/>
										</div>
										<div className="col-md-3 fv-row">
											<label className="form-label required fs-6 fw-bold">
												DRP/DRP I/C Name
											</label>
											<Field
												as="select"
												name="drp_employee_id"
												className="form-select"
												disabled={isView || !values.district_id}
											>
												<option value="">Select DRP</option>
												{drpList.map((d) => (
													<option key={d.id} value={d.id}>
														{d.name}
													</option>
												))}
											</Field>
											<ErrorMessage
												name="drp_employee_id"
												component="div"
												className="text-danger fs-8"
											/>
										</div>
										<div className="col-md-3 fv-row">
											<label className="form-label required fs-6 fw-bold">HLC No.</label>
											<Field name="hlc_no" className="form-control" disabled={isView} />
											<ErrorMessage
												name="hlc_no"
												component="div"
												className="text-danger fs-8"
											/>
										</div>
										<div className="col-md-3 fv-row">
											<label className="form-label required fs-6 fw-bold">HLC Date</label>
											<Field
												type="date"
												name="hlc_date"
												className="form-control"
												disabled={isView}
											/>
											<ErrorMessage
												name="hlc_date"
												component="div"
												className="text-danger fs-8"
											/>
										</div>
									</div>
									<div className="row g-9 mb-5">
										<div className="col-md-3 fv-row">
											<label className="form-label required fs-6 fw-bold">
												Proceeding No.
											</label>
											<Field
												name="proceeding_no"
												className="form-control"
												disabled={isView}
											/>
											<ErrorMessage
												name="proceeding_no"
												component="div"
												className="text-danger fs-8"
											/>
										</div>
										<div className="col-md-3 fv-row">
											<label className="form-label required fs-6 fw-bold">
												Proceeding Date
											</label>
											<Field
												type="date"
												name="proceeding_date"
												className="form-control"
												disabled={isView}
											/>
											<ErrorMessage
												name="proceeding_date"
												component="div"
												className="text-danger fs-8"
											/>
										</div>
										<div className="col-md-3 fv-row">
											<label className="form-label fw-bold">No. of Paras Placed</label>
											<Field
												type="number"
												name="paras_placed"
												className="form-control"
												min={0}
												disabled={isView}
											/>
										</div>
										<div className="col-md-3 fv-row">
											<label className="form-label fw-bold">No. of Paras Closed</label>
											<Field
												type="number"
												name="paras_closed"
												className="form-control"
												min={0}
												disabled={isView}
											/>
										</div>
									</div>
									<div className="row g-9">
										<div className="col-md-3 fv-row">
											<label className="form-label fw-bold">Pending</label>
											<input
												type="number"
												className="form-control bg-light"
												value={pending}
												readOnly
											/>
										</div>
									</div>
								</div>

								{/* Para Details */}
								<div className="mb-10 border border-dashed border-gray-300 p-8 rounded-3">
									<h4 className="fw-bold text-gray-800 mb-6">
										Para Details ({targetScheme})
									</h4>
									<div className="row g-9 mb-5">
										<div className="col-md-3 fv-row">
											<label className="form-label fw-bold">FM - No. of Paras</label>
											<Field
												type="number"
												name="fm_paras"
												className="form-control"
												min={0}
												disabled={isView}
											/>
										</div>
										<div className="col-md-3 fv-row">
											<label className="form-label fw-bold">FM - Amount</label>
											<Field
												type="number"
												name="fm_amount"
												className="form-control"
												min={0}
												disabled={isView}
											/>
										</div>
										<div className="col-md-3 fv-row">
											<label className="form-label fw-bold">FD - No. of Paras</label>
											<Field
												type="number"
												name="fd_paras"
												className="form-control"
												min={0}
												disabled={isView}
											/>
										</div>
										<div className="col-md-3 fv-row">
											<label className="form-label fw-bold">FD - Amount</label>
											<Field
												type="number"
												name="fd_amount"
												className="form-control"
												min={0}
												disabled={isView}
											/>
										</div>
									</div>
									<div className="row g-9">
										<div className="col-md-3 fv-row">
											<label className="form-label fw-bold">PV - No. of Paras</label>
											<Field
												type="number"
												name="pv_paras"
												className="form-control"
												min={0}
												disabled={isView}
											/>
										</div>
										<div className="col-md-3 fv-row">
											<label className="form-label fw-bold">PV - Amount</label>
											<Field
												type="number"
												name="pv_amount"
												className="form-control"
												min={0}
												disabled={isView}
											/>
										</div>
										<div className="col-md-3 fv-row">
											<label className="form-label fw-bold">GR - No. of Paras</label>
											<Field
												type="number"
												name="gr_paras"
												className="form-control"
												min={0}
												disabled={isView}
											/>
										</div>
										<div className="col-md-3 fv-row">
											<label className="form-label fw-bold">GR - Amount</label>
											<Field
												type="number"
												name="gr_amount"
												className="form-control"
												min={0}
												disabled={isView}
											/>
										</div>
									</div>
								</div>

								{/* Action & Recovery */}
								<div className="mb-10 border border-dashed border-gray-300 p-8 rounded-3">
									<h4 className="fw-bold text-gray-800 mb-6">Action & Recovery</h4>
									<div className="row g-9 mb-5">
										<div className="col-md-4 fv-row">
											<label className="form-label fw-bold">Recovered Amount in HLC</label>
											<Field
												type="number"
												name="recovered_amount"
												className="form-control"
												min={0}
												disabled={isView}
											/>
										</div>
									</div>
									<div className="row g-9 mb-5">
										<div className="col-md-4 fv-row">
											<label className="form-label fw-bold d-block">FIR</label>
											<div className="d-flex align-items-center gap-4">
												<label className="form-check form-check-custom form-check-solid">
													<Field
														type="radio"
														name="fir"
														value="1"
														className="form-check-input"
														disabled={isView}
													/>
													<span className="form-check-label ms-2">Yes</span>
												</label>
												<label className="form-check form-check-custom form-check-solid">
													<Field
														type="radio"
														name="fir"
														value="2"
														className="form-check-input"
														disabled={isView}
													/>
													<span className="form-check-label ms-2">No</span>
												</label>
											</div>
										</div>
										<div className="col-md-4 fv-row">
											<label className="form-label fw-bold d-block">Charges</label>
											<div className="d-flex align-items-center gap-4">
												<label className="form-check form-check-custom form-check-solid">
													<Field
														type="radio"
														name="charges"
														value="1"
														className="form-check-input"
														disabled={isView}
													/>
													<span className="form-check-label ms-2">Yes</span>
												</label>
												<label className="form-check form-check-custom form-check-solid">
													<Field
														type="radio"
														name="charges"
														value="2"
														className="form-check-input"
														disabled={isView}
													/>
													<span className="form-check-label ms-2">No</span>
												</label>
											</div>
										</div>
									</div>
									<div className="mb-5">
										<label className="form-label fw-bold">Action Taken Details</label>
										<Field
											as="textarea"
											name="action_taken_details"
											rows={3}
											className="form-control"
											disabled={isView}
										/>
									</div>
								</div>

								{/* Upload + Actions */}
								<div className="mb-5 border border-dashed border-gray-300 p-8 rounded-3">
									<FileUpload
										label="HLC Minutes Upload (Max 5MB)"
										value={values.hlc_minutes_file}
										disabled={isView}
										onChange={(val) =>
											setFieldValue("hlc_minutes_file", val as string | null)
										}
										onUpload={(file) => uploadSchemeReport(file, "hlc-minutes")}
										accept=".pdf,.doc,.docx"
										maxSizeMB={5}
										description="Max 5MB (PDF or DOCX format)"
									/>

									{values.registration_no && (
										<div className="mt-5">
											<span className="badge badge-light-success fs-6 p-3">
												Registration No.: {values.registration_no}
											</span>
										</div>
									)}

									{!isView && (
										<div className="d-flex justify-content-end gap-3 mt-8">
											<button
												type="button"
												className="btn btn-light-primary"
												disabled={!canGenerate || generating}
												onClick={handleGenerate}
											>
												{generating ? "Generating..." : "Generate Registration No."}
											</button>
											<button
												type="submit"
												className="btn btn-primary"
												disabled={!values.registration_no || isSubmitting}
											>
												{isSubmitting ? "Submitting..." : "Verify & Submit"}
											</button>
										</div>
									)}
								</div>
							</Form>
						);
					}}
				</Formik>
				)}
			</div>
		</div>
	);
};

export default HLCForm;
